/**
 * Queue Monitoring Dashboard
 *
 * Real-time queue metrics, performance analytics, and system health monitoring
 * for administrators.
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';
import {
  AlertCircle, CheckCircle, XCircle, Clock, Users, Activity,
  TrendingUp, TrendingDown, RefreshCw, Settings, Bell
} from 'lucide-react';
import { useQueueMetrics } from '@/hooks/useQueueMetrics';
import { useWebSocket } from '@/hooks/useWebSocket';
import { formatDistanceToNow } from 'date-fns';

interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  trend?: 'up' | 'down' | 'stable';
  trendValue?: string;
  status?: 'success' | 'warning' | 'error';
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  subtitle,
  icon,
  trend,
  trendValue,
  status = 'success'
}) => {
  const statusColors = {
    success: 'text-green-600',
    warning: 'text-yellow-600',
    error: 'text-red-600'
  };

  const trendIcons = {
    up: <TrendingUp className="w-4 h-4 text-green-500" />,
    down: <TrendingDown className="w-4 h-4 text-red-500" />,
    stable: <Activity className="w-4 h-4 text-gray-500" />
  };

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className={`text-2xl font-bold ${statusColors[status]}`}>
              {value}
            </p>
            {subtitle && (
              <p className="text-xs text-muted-foreground">{subtitle}</p>
            )}
            {trend && (
              <div className="flex items-center gap-2 mt-2">
                {trendIcons[trend]}
                <span className="text-xs">{trendValue}</span>
              </div>
            )}
          </div>
          <div className="text-muted-foreground">{icon}</div>
        </div>
      </CardContent>
    </Card>
  );
};

const QueueDashboard: React.FC = () => {
  const [refreshInterval, setRefreshInterval] = useState(5000);
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Fetch queue metrics
  const {
    currentMetrics,
    performanceStats,
    alerts,
    recentIssues,
    cacheMetrics,
    workerUtilization,
    processingTimeDistribution,
    tierMetrics,
    securityOverview,
    apiQuota,
    systemHealth,
    isLoading,
    refetch
  } = useQueueMetrics(autoRefresh ? refreshInterval : null);

  // WebSocket for real-time updates
  useWebSocket({
    enabled: true,
    onMessage: (message) => {
      if (message.type === 'metrics_update') {
        refetch();
      }
    }
  });

  // Auto-refresh effect
  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(refetch, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, refreshInterval, refetch]);

  // Calculate key metrics
  const queueDepth = currentMetrics?.queue_depth || 0;
  const jobsPerMinute = currentMetrics?.jobs_per_minute || 0;
  const avgProcessingTime = currentMetrics?.average_processing_time || 0;
  const failureRate = currentMetrics?.failure_rate || 0;
  const activeWorkers = currentMetrics?.active_workers || 0;

  // Determine health status
  const getHealthStatus = () => {
    if (failureRate > 25 || activeWorkers === 0) return 'error';
    if (failureRate > 10 || queueDepth > 100) return 'warning';
    return 'success';
  };

  const healthStatus = getHealthStatus();
  const healthColors = {
    success: 'bg-green-100 text-green-800',
    warning: 'bg-yellow-100 text-yellow-800',
    error: 'bg-red-100 text-red-800'
  };

  // Pie chart data for tier distribution
  const tierChartData = tierMetrics
    ? Object.entries(tierMetrics).map(([tier, data]: [string, any]) => ({
        name: tier.charAt(0).toUpperCase() + tier.slice(1),
        value: data.total_jobs_24h,
        success_rate: data.success_rate
      }))
    : [];

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Queue Monitoring Dashboard</h1>
          <p className="text-muted-foreground">
            Real-time queue metrics and system health
          </p>
        </div>
        <div className="flex items-center gap-4">
          <Badge className={healthColors[healthStatus]}>
            {healthStatus === 'success' && <CheckCircle className="w-4 h-4 mr-1" />}
            {healthStatus === 'warning' && <AlertCircle className="w-4 h-4 mr-1" />}
            {healthStatus === 'error' && <XCircle className="w-4 h-4 mr-1" />}
            System {healthStatus}
          </Badge>
          <Button
            variant="outline"
            size="sm"
            onClick={refetch}
            disabled={isLoading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            variant={autoRefresh ? 'default' : 'outline'}
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            Auto-refresh: {autoRefresh ? 'ON' : 'OFF'}
          </Button>
        </div>
      </div>

      {/* Active Alerts */}
      {alerts && alerts.length > 0 && (
        <div className="space-y-2">
          {alerts.map((alert, index) => (
            <Alert key={index} variant={alert.level === 'critical' ? 'destructive' : 'default'}>
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>{alert.level.toUpperCase()}: {alert.type}</AlertTitle>
              <AlertDescription>
                {alert.message}
                {alert.value && alert.threshold && (
                  <span className="ml-2 text-sm">
                    (Current: {alert.value}, Threshold: {alert.threshold})
                  </span>
                )}
              </AlertDescription>
            </Alert>
          ))}
        </div>
      )}

      {/* Recent Issues */}
      {recentIssues && recentIssues.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-foreground">Recent Issues</h3>
          {recentIssues.map((issue, index) => (
            <Alert key={index} variant={issue.severity === 'critical' ? 'destructive' : 'default'} className="text-foreground">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle className="text-foreground">{issue.severity.toUpperCase()}: {issue.type}</AlertTitle>
              <AlertDescription className="text-foreground">
                {issue.message}
                <span className="ml-2 text-xs text-muted-foreground">
                  {new Date(issue.timestamp).toLocaleString()}
                </span>
              </AlertDescription>
            </Alert>
          ))}
        </div>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <MetricCard
          title="Queue Depth"
          value={queueDepth}
          subtitle="Jobs waiting"
          icon={<Clock className="w-8 h-8" />}
          status={queueDepth > 100 ? 'error' : queueDepth > 50 ? 'warning' : 'success'}
          trend={queueDepth > 50 ? 'up' : 'stable'}
          trendValue={`${queueDepth > 50 ? '+' : ''}${queueDepth - 50}`}
        />
        <MetricCard
          title="Jobs/Minute"
          value={jobsPerMinute.toFixed(1)}
          subtitle="Processing rate"
          icon={<Activity className="w-8 h-8" />}
          status="success"
        />
        <MetricCard
          title="Avg Processing"
          value={`${avgProcessingTime.toFixed(0)}s`}
          subtitle="Per job"
          icon={<Clock className="w-8 h-8" />}
          status={avgProcessingTime > 300 ? 'warning' : 'success'}
        />
        <MetricCard
          title="Failure Rate"
          value={`${failureRate.toFixed(1)}%`}
          subtitle="Last 5 minutes"
          icon={<XCircle className="w-8 h-8" />}
          status={failureRate > 10 ? 'error' : failureRate > 5 ? 'warning' : 'success'}
          trend={failureRate > 5 ? 'up' : 'down'}
        />
        <MetricCard
          title="Active Workers"
          value={activeWorkers}
          subtitle="Processing jobs"
          icon={<Users className="w-8 h-8" />}
          status={activeWorkers === 0 ? 'error' : activeWorkers < 2 ? 'warning' : 'success'}
        />
      </div>

      {/* Detailed Metrics Tabs */}
      <Tabs defaultValue="performance" className="space-y-4">
        <TabsList>
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="tiers">User Tiers</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
          <TabsTrigger value="system">System Health</TabsTrigger>
        </TabsList>

        <TabsContent value="performance" className="space-y-4">
          {/* Performance Summary */}
          {performanceStats && (
            <Card>
              <CardHeader>
                <CardTitle>Performance Summary (24h)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Avg Queue Depth</p>
                    <p className="text-lg font-bold">{performanceStats.avg_queue_depth?.toFixed(1) || '0'}</p>
                    <p className="text-xs text-muted-foreground">
                      Min: {performanceStats.min_queue_depth ?? 0} | Max: {performanceStats.max_queue_depth ?? 0}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Avg Throughput</p>
                    <p className="text-lg font-bold">{performanceStats.avg_jobs_per_minute?.toFixed(2) || '0'}/min</p>
                    <p className="text-xs text-muted-foreground">
                      Peak: {performanceStats.peak_jobs_per_minute?.toFixed(2) || '0'}/min
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Avg Response Time</p>
                    <p className="text-lg font-bold">{performanceStats.avg_processing_time?.toFixed(1) || '0'}s</p>
                    <p className="text-xs text-muted-foreground">
                      Min: {performanceStats.min_processing_time?.toFixed(1) || '0'}s | Max: {performanceStats.max_processing_time?.toFixed(1) || '0'}s
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Avg Error Rate</p>
                    <p className="text-lg font-bold">{performanceStats.avg_failure_rate?.toFixed(2) || '0'}%</p>
                    <p className="text-xs text-muted-foreground">
                      Max: {performanceStats.max_failure_rate?.toFixed(2) || '0'}%
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Total Jobs</p>
                    <p className="text-lg font-bold">{performanceStats.total_jobs?.toLocaleString() || '0'}</p>
                    <p className="text-xs text-muted-foreground">
                      Completed: {performanceStats.completed_jobs?.toLocaleString() || '0'}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Success Rate</p>
                    <p className={`text-lg font-bold ${(performanceStats.success_rate ?? 0) > 95 ? 'text-green-600' : (performanceStats.success_rate ?? 0) > 85 ? 'text-yellow-600' : 'text-red-600'}`}>
                      {performanceStats.success_rate?.toFixed(1) || '0'}%
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Failed: {performanceStats.failed_jobs?.toLocaleString() || '0'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Queue Depth Over Time</CardTitle>
            </CardHeader>
            <CardContent>
              {performanceStats?.time_series && performanceStats.time_series.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={performanceStats.time_series}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="timestamp"
                        tickFormatter={(value) => new Date(value).toLocaleTimeString()}
                      />
                      <YAxis domain={[0, 'auto']} allowDataOverflow={false} />
                      <Tooltip
                        labelFormatter={(value) => new Date(value).toLocaleString()}
                        contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)' }}
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="queue_depth"
                        stroke="#8884d8"
                        strokeWidth={2}
                        name="Queue Depth"
                        dot={false}
                        isAnimationActive={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="jobs_per_minute"
                        stroke="#82ca9d"
                        strokeWidth={2}
                        name="Jobs/Min"
                        dot={false}
                        isAnimationActive={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="active_workers"
                        stroke="#ffc658"
                        strokeWidth={2}
                        name="Active Workers"
                        dot={false}
                        isAnimationActive={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                  {performanceStats.total_jobs === 0 && (
                    <Alert className="mt-4">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        No job activity detected in the last 24 hours. The graph shows {performanceStats.data_points} data points, but all metrics are at zero.
                      </AlertDescription>
                    </Alert>
                  )}
                </>
              ) : (
                <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                  <div className="text-center space-y-2">
                    <Activity className="w-12 h-12 mx-auto opacity-50" />
                    <p>No performance data available</p>
                    <p className="text-xs">Data points: {performanceStats?.data_points || 0}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Response Time Over Time</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart data={performanceStats?.time_series || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="timestamp"
                      tickFormatter={(value) => new Date(value).toLocaleTimeString()}
                    />
                    <YAxis label={{ value: 'Response Time (s)', angle: -90, position: 'insideLeft' }} />
                    <Tooltip
                      labelFormatter={(value) => new Date(value).toLocaleString()}
                      formatter={(value: number) => [`${value.toFixed(2)}s`, 'Response Time']}
                    />
                    <Area
                      type="monotone"
                      dataKey="processing_time"
                      stroke="#8884d8"
                      fill="#8884d8"
                      fillOpacity={0.6}
                      name="Processing Time (s)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Failure Rate Trend (Hourly)</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={performanceStats?.failure_trend || performanceStats?.time_series || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey={performanceStats?.failure_trend ? "hour" : "timestamp"}
                      tickFormatter={(value) => {
                        const date = new Date(value);
                        return performanceStats?.failure_trend
                          ? date.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit' })
                          : date.toLocaleTimeString();
                      }}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis label={{ value: 'Failure Rate (%)', angle: -90, position: 'insideLeft' }} />
                    <Tooltip
                      labelFormatter={(value) => new Date(value).toLocaleString()}
                    />
                    <Legend />
                    {performanceStats?.failure_trend ? (
                      <>
                        <Line
                          type="monotone"
                          dataKey="avg_failure_rate"
                          stroke="#ff7300"
                          name="Avg Failure Rate (%)"
                          strokeWidth={2}
                          dot={false}
                        />
                        <Line
                          type="monotone"
                          dataKey="max_failure_rate"
                          stroke="#ff0000"
                          name="Max Failure Rate (%)"
                          strokeWidth={1}
                          strokeDasharray="3 3"
                          dot={false}
                        />
                      </>
                    ) : (
                      <Line
                        type="monotone"
                        dataKey="failure_rate"
                        stroke="#ff7300"
                        name="Failure Rate (%)"
                        dot={false}
                      />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="tiers" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Jobs by Tier</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={tierChartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={(entry) => `${entry.name}: ${entry.value}`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {tierChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Tier Performance</CardTitle>
              </CardHeader>
              <CardContent>
                {tierMetrics && (
                  <div className="space-y-4">
                    {Object.entries(tierMetrics).map(([tier, data]: [string, any]) => (
                      <div key={tier} className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="font-medium capitalize">{tier}</span>
                          <span>{data.success_rate?.toFixed(1) || '0'}% success</span>
                        </div>
                        <Progress value={data.success_rate || 0} className="h-2" />
                        <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground mt-2">
                          <span>{data.total_jobs_24h || 0} jobs (24h)</span>
                          <span>{data.user_count || 0} users</span>
                          <span>{data.avg_wait_time?.toFixed(1) || '0'}s wait</span>
                          <span>{data.avg_processing_time?.toFixed(1) || '0'}s process</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="history" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>24-Hour Statistics</CardTitle>
            </CardHeader>
            <CardContent>
              {performanceStats && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Total Jobs</p>
                    <p className="text-2xl font-bold">{performanceStats.total_jobs}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Completed</p>
                    <p className="text-2xl font-bold text-green-600">
                      {performanceStats.completed_jobs}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Failed</p>
                    <p className="text-2xl font-bold text-red-600">
                      {performanceStats.failed_jobs}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Success Rate</p>
                    <p className="text-2xl font-bold">
                      {performanceStats.success_rate?.toFixed(1)}%
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Performance History (24h) Graph */}
          <Card>
            <CardHeader>
              <CardTitle>Performance History (24h)</CardTitle>
            </CardHeader>
            <CardContent>
              {performanceStats?.time_series && performanceStats.time_series.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={400}>
                    <AreaChart data={performanceStats.time_series}>
                      <defs>
                        <linearGradient id="colorQueue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#8884d8" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorJobs" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#82ca9d" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorWorkers" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#ffc658" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#ffc658" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="timestamp"
                        tickFormatter={(value) => {
                          const date = new Date(value);
                          return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                        }}
                      />
                      <YAxis />
                      <Tooltip
                        labelFormatter={(value) => new Date(value).toLocaleString()}
                        contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '1px solid #ccc' }}
                      />
                      <Legend />
                      <Area
                        type="monotone"
                        dataKey="queue_depth"
                        stroke="#8884d8"
                        strokeWidth={2}
                        fillOpacity={1}
                        fill="url(#colorQueue)"
                        name="Queue Depth"
                        isAnimationActive={false}
                      />
                      <Area
                        type="monotone"
                        dataKey="jobs_per_minute"
                        stroke="#82ca9d"
                        strokeWidth={2}
                        fillOpacity={1}
                        fill="url(#colorJobs)"
                        name="Jobs/Min"
                        isAnimationActive={false}
                      />
                      <Area
                        type="monotone"
                        dataKey="active_workers"
                        stroke="#ffc658"
                        strokeWidth={2}
                        fillOpacity={1}
                        fill="url(#colorWorkers)"
                        name="Active Workers"
                        isAnimationActive={false}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                  {performanceStats.total_jobs === 0 && (
                    <Alert className="mt-4">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        No job activity detected in the last 24 hours. The graph shows {performanceStats.data_points} data points collected, but all metrics are at zero (no jobs processed).
                      </AlertDescription>
                    </Alert>
                  )}
                </>
              ) : (
                <div className="flex items-center justify-center h-[400px] text-muted-foreground">
                  <div className="text-center space-y-2">
                    <Activity className="w-12 h-12 mx-auto opacity-50" />
                    <p className="font-medium">No performance history data available</p>
                    <p className="text-sm">Metrics collection may still be initializing</p>
                    {performanceStats && (
                      <p className="text-xs">Data points collected: {performanceStats.data_points || 0}</p>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="system">
          {/* Component Health Status */}
          {systemHealth && (
            <Card className="mb-4">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  Component Health Status
                  <Badge variant={
                    systemHealth.overall === 'healthy' ? 'default' :
                    systemHealth.overall === 'degraded' ? 'secondary' : 'destructive'
                  }>
                    {systemHealth.overall.toUpperCase()}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Database */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Database</span>
                      <Badge variant={
                        systemHealth.components.database.status === 'healthy' ? 'default' :
                        systemHealth.components.database.status === 'degraded' ? 'secondary' : 'destructive'
                      }>
                        {systemHealth.components.database.status}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {systemHealth.components.database.message}
                    </p>
                  </div>

                  {/* Redis */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Redis</span>
                      <Badge variant={
                        systemHealth.components.redis.status === 'healthy' ? 'default' :
                        systemHealth.components.redis.status === 'degraded' ? 'secondary' : 'destructive'
                      }>
                        {systemHealth.components.redis.status}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {systemHealth.components.redis.message}
                    </p>
                  </div>

                  {/* ElevenLabs */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">ElevenLabs API</span>
                      <Badge variant={
                        systemHealth.components.elevenlabs.status === 'healthy' ? 'default' :
                        systemHealth.components.elevenlabs.status === 'degraded' ? 'secondary' : 'destructive'
                      }>
                        {systemHealth.components.elevenlabs.status}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {systemHealth.components.elevenlabs.message}
                    </p>
                  </div>

                  {/* Celery Workers */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Celery Workers</span>
                      <Badge variant={
                        systemHealth.components.celery.status === 'healthy' ? 'default' :
                        systemHealth.components.celery.status === 'degraded' ? 'secondary' : 'destructive'
                      }>
                        {systemHealth.components.celery.status}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {systemHealth.components.celery.message}
                    </p>
                    {systemHealth.components.celery.workers && systemHealth.components.celery.workers.length > 0 && (
                      <div className="text-xs text-muted-foreground mt-1">
                        Workers: {systemHealth.components.celery.workers.join(', ')}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Worker Utilization</CardTitle>
              </CardHeader>
              <CardContent>
                {workerUtilization ? (
                  <div className="space-y-4">
                    <div className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span>Utilization</span>
                        <span className="font-medium">
                          {workerUtilization.utilization_percentage.toFixed(1)}%
                        </span>
                      </div>
                      <Progress value={workerUtilization.utilization_percentage} className="h-2" />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>{workerUtilization.active_tasks} active tasks</span>
                        <span>{workerUtilization.total_workers} workers / {workerUtilization.max_capacity} capacity</span>
                      </div>
                    </div>
                    {workerUtilization.worker_details && (
                      <div className="space-y-2 mt-4">
                        <p className="text-sm font-medium">Worker Status:</p>
                        {Object.entries(workerUtilization.worker_details).map(([worker, details]) => (
                          <div key={worker} className="flex items-center justify-between text-sm">
                            <span className="truncate">{worker}</span>
                            <Badge variant={details.status === 'busy' ? 'default' : 'secondary'}>
                              {details.status} ({details.active_tasks} tasks)
                            </Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No worker data available</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Resource Usage</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {apiQuota && (
                    <div className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span>API Costs ({apiQuota.current_month || 'N/A'})</span>
                        <span className="font-medium">${apiQuota.total_cost?.toFixed(2) || '0.00'}</span>
                      </div>
                      <div className="text-xs text-muted-foreground space-y-1 mt-2">
                        <div className="flex justify-between">
                          <span>OpenAI:</span>
                          <span>${apiQuota.openai_cost?.toFixed(2) || '0.00'} ({apiQuota.openai_tokens?.toLocaleString() || '0'} tokens)</span>
                        </div>
                        <div className="flex justify-between">
                          <span>ElevenLabs:</span>
                          <span>${apiQuota.elevenlabs_cost?.toFixed(2) || '0.00'} ({apiQuota.elevenlabs_characters?.toLocaleString() || '0'} chars)</span>
                        </div>
                      </div>
                    </div>
                  )}
                  {cacheMetrics && (
                    <div className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span>Cache Hit Rate</span>
                        <span className="font-medium">{cacheMetrics.hit_rate?.toFixed(1) || '0'}%</span>
                      </div>
                      <Progress value={cacheMetrics.hit_rate || 0} className="h-2" />
                      <div className="text-xs text-muted-foreground mt-1">
                        {cacheMetrics.cache_hits?.toLocaleString() || '0'} hits / {cacheMetrics.cache_misses?.toLocaleString() || '0'} misses
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Processing Time Distribution */}
          {processingTimeDistribution && processingTimeDistribution.total_jobs > 0 && (
            <Card className="mt-4">
              <CardHeader>
                <CardTitle>Processing Time Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">P50 (Median)</p>
                    <p className="text-lg font-bold">{processingTimeDistribution.p50.toFixed(1)}s</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">P75</p>
                    <p className="text-lg font-bold">{processingTimeDistribution.p75.toFixed(1)}s</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">P90</p>
                    <p className="text-lg font-bold">{processingTimeDistribution.p90.toFixed(1)}s</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">P95</p>
                    <p className="text-lg font-bold">{processingTimeDistribution.p95.toFixed(1)}s</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">P99</p>
                    <p className="text-lg font-bold">{processingTimeDistribution.p99.toFixed(1)}s</p>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={processingTimeDistribution.distribution ? Object.entries(processingTimeDistribution.distribution).map(([bucket, count]) => ({
                    bucket,
                    count
                  })) : []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="bucket" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#8884d8" name="Jobs" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Security Overview (Admin Only) */}
          {securityOverview && (
            <Card className="mt-4">
              <CardHeader>
                <CardTitle>Security Overview</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Failed Logins (24h)</p>
                    <p className="text-2xl font-bold">{securityOverview.failed_logins_24h}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Failed Logins (7d)</p>
                    <p className="text-2xl font-bold">{securityOverview.failed_logins_7d}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Security Status</p>
                    <Badge variant={
                      securityOverview.status === 'healthy' ? 'default' :
                      securityOverview.status === 'warning' ? 'secondary' : 'destructive'
                    }>
                      {securityOverview.status.toUpperCase()}
                    </Badge>
                  </div>
                </div>
                {securityOverview.suspicious_ips && securityOverview.suspicious_ips.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm font-medium mb-2">Suspicious IPs:</p>
                    <div className="space-y-1">
                      {securityOverview.suspicious_ips.slice(0, 5).map((ip, idx) => (
                        <div key={idx} className="flex justify-between text-sm">
                          <span className="font-mono">{ip.ip}</span>
                          <span className="text-muted-foreground">{ip.event_count} events</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default QueueDashboard;